import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, Switch, Card, CardContent,
  Typography, LinearProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, IconButton, TextField, Checkbox, FormControlLabel, FormGroup,
  Avatar, Divider
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  HourglassEmpty as PendingIcon,
  Edit as EditIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const PRODUCTS = [
  { key: 'site_dna', label: 'Site DNA', color: vortxColors.teal },
  { key: 'flux_ev', label: 'Flux EV', color: vortxColors.success },
  { key: 'flux_phd', label: 'Flux PhD', color: vortxColors.stone },
  { key: 'flux_ems', label: 'Flux EMS', color: vortxColors.gold }
];

const TeamSeatsPage = () => {
  const { currentUser, userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [seats, setSeats] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [seatSummary, setSeatSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const isSuperAdmin = userProfile?.is_super_admin;

  // Merge users with their seats data for easy access
  const usersWithSeats = useMemo(() => {
    return users.map(user => {
      const userSeats = seats.filter(s => s.user_id === user.id);
      return {
        ...user,
        seats: userSeats.map(s => s.product),
        seatDetails: userSeats // Include full seat objects with IDs
      };
    });
  }, [users, seats]);

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);
      const token = await currentUser.getIdToken();

      // Fetch users
      const usersResponse = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!usersResponse.ok) throw new Error('Failed to fetch users');
      const usersData = await usersResponse.json();
      setUsers(usersData.users);

      // Fetch pending invites
      try {
        const invitesResponse = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/invites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (invitesResponse.ok) {
          const invitesData = await invitesResponse.json();
          setPendingInvites(invitesData.invites || []);
        } else if (invitesResponse.status !== 404) {
          console.warn('Failed to fetch invites:', invitesResponse.status);
        }
      } catch (inviteErr) {
        console.warn('Invites not available:', inviteErr.message);
      }

      // Fetch seats data (includes seat IDs for editing)
      try {
        const seatsResponse = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/seats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (seatsResponse.ok) {
          const seatsData = await seatsResponse.json();
          setSeats(seatsData.seats || []);
          setSeatSummary(seatsData.summary || null);
        } else if (seatsResponse.status !== 404) {
          console.warn('Failed to fetch seats:', seatsResponse.status);
        }
      } catch (seatErr) {
        console.warn('Seats not available:', seatErr.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [fetchData, currentUser]);

  const handleAdminToggle = async (userId, isAdmin) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_company_admin: isAdmin })
      });

      if (!response.ok) throw new Error('Failed to update user');

      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDemoToggle = async (userId, isDemo) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_demo: isDemo })
      });

      if (!response.ok) throw new Error('Failed to update user');

      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/invites/${inviteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to cancel invite');

      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleChip = (user) => {
    if (user.is_super_admin) {
      return <Chip label="Super Admin" size="small" sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }} />;
    }
    if (user.is_company_admin) {
      return <Chip label="Admin" size="small" sx={{ bgcolor: alpha(vortxColors.teal, 0.2), color: vortxColors.teal }} />;
    }
    return <Chip label="Member" size="small" variant="outlined" sx={{ borderColor: vortxColors.stone, color: vortxColors.stone }} />;
  };

  const getProductAccess = (user) => {
    const userSeats = user.is_super_admin ? PRODUCTS.map(p => p.key) : (user.seats || []);

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {PRODUCTS.map((product) => {
          const hasSeat = userSeats.includes(product.key);
          if (!hasSeat) return null;
          return (
            <Chip
              key={product.key}
              label={product.label}
              size="small"
              icon={<CheckIcon sx={{ fontSize: 14 }} />}
              sx={{
                bgcolor: alpha(product.color, 0.15),
                color: product.color,
                borderColor: product.color,
                fontSize: '0.7rem',
                height: 24,
                '& .MuiChip-icon': { color: product.color }
              }}
            />
          );
        })}
        {userSeats.length === 0 && (
          <Typography variant="caption" color="text.secondary">No access</Typography>
        )}
      </Box>
    );
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Team & Seats">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress sx={{ color: vortxColors.gold }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Team & Seats">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Seat Summary Cards */}
      {seatSummary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {PRODUCTS.map((product) => {
            const usage = seatSummary[product.key] || { used: 0, total: 0 };
            const percentage = usage.total > 0 ? (usage.used / usage.total) * 100 : 0;

            return (
              <Card key={product.key} sx={{ flex: '1 1 200px', minWidth: 200 }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: product.color, fontWeight: 600 }}>
                      {product.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {usage.used}/{usage.total}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha(product.color, 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: product.color,
                        borderRadius: 3
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {usage.total - usage.used} seats available
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Team Members Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Team Members
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {users.length} member{users.length !== 1 ? 's' : ''} in your organization
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchData} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
                size="small"
                sx={{
                  bgcolor: vortxColors.gold,
                  color: vortxColors.green,
                  '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
                }}
              >
                Invite
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product Access</TableCell>
                  {isSuperAdmin && <TableCell sx={{ fontWeight: 600 }} align="center">Company Admin</TableCell>}
                  {isSuperAdmin && <TableCell sx={{ fontWeight: 600 }} align="center">Demo</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersWithSeats.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleEditUser(user)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: vortxColors.teal,
                            fontSize: '0.875rem'
                          }}
                        >
                          {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{getRoleChip(user)}</TableCell>
                    <TableCell>{getProductAccess(user)}</TableCell>
                    {isSuperAdmin && (
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={user.is_company_admin || false}
                          onChange={(e) => handleAdminToggle(user.id, e.target.checked)}
                          disabled={user.is_super_admin}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: vortxColors.teal
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: vortxColors.teal
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    {isSuperAdmin && (
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={user.is_demo || false}
                          onChange={(e) => handleDemoToggle(user.id, e.target.checked)}
                          disabled={user.is_super_admin}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: vortxColors.gold
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: vortxColors.gold
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit team member">
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          sx={{ color: vortxColors.teal }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {usersWithSeats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 7 : 5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No team members found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pending Invites Section */}
      {pendingInvites.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PendingIcon sx={{ color: vortxColors.gold }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Pending Invites
              </Typography>
              <Chip
                label={pendingInvites.length}
                size="small"
                sx={{ bgcolor: alpha(vortxColors.gold, 0.2), color: vortxColors.gold }}
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Products</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invited By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingInvites.map((invite) => (
                    <TableRow key={invite.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {invite.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(invite.products || ['site_dna']).map((productKey) => {
                            const product = PRODUCTS.find(p => p.key === productKey);
                            return product ? (
                              <Chip
                                key={productKey}
                                label={product.label}
                                size="small"
                                sx={{
                                  bgcolor: alpha(product.color, 0.15),
                                  color: product.color,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            ) : null;
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invite.invited_by_email || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {invite.created_at ? new Date(invite.created_at).toLocaleDateString() : 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Cancel invite">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelInvite(invite.id)}
                            sx={{ color: vortxColors.stone, '&:hover': { color: '#f44336' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* No seat data message */}
      {!seatSummary && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Seat allocation is not configured for your organization. All team members currently have access to Site DNA.
          Contact support to set up seat limits and multi-product access.
        </Alert>
      )}

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        currentUser={currentUser}
        onInviteSent={fetchData}
      />

      {/* Edit Member Dialog */}
      <EditMemberDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
        onSaved={fetchData}
      />
    </AdminLayout>
  );
};

const InviteDialog = ({ open, onClose, currentUser, onInviteSent }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(['site_dna']); // Default to Site DNA
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleProductToggle = (productKey) => {
    setSelectedProducts(prev =>
      prev.includes(productKey)
        ? prev.filter(p => p !== productKey)
        : [...prev, productKey]
    );
  };

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    setSending(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/invites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: name.trim() || undefined,
          products: selectedProducts
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invite');
      }

      setSuccess(true);
      setEmail('');
      setName('');
      if (onInviteSent) onInviteSent();

      // Close after short delay
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setName('');
    setSelectedProducts(['site_dna']);
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite Team Member</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Invitation sent! They will receive access when they sign up with this email.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Enter the email address of the person you want to invite. When they sign up, they will automatically join your team.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              label="Email Address"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sending}
              placeholder="colleague@company.com"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Full Name"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              disabled={sending}
              placeholder="John Smith (optional)"
              helperText="Pre-fills their profile when they sign up"
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Product Access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select which products this user will have access to:
            </Typography>
            <FormGroup>
              {PRODUCTS.map((product) => (
                <FormControlLabel
                  key={product.key}
                  control={
                    <Checkbox
                      checked={selectedProducts.includes(product.key)}
                      onChange={() => handleProductToggle(product.key)}
                      disabled={sending}
                      sx={{
                        color: product.color,
                        '&.Mui-checked': { color: product.color }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{product.label}</Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={sending || !email}
            sx={{ bgcolor: vortxColors.gold, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) } }}
          >
            {sending ? <CircularProgress size={24} /> : 'Send Invite'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const EditMemberDialog = ({ open, onClose, user, currentUser, isSuperAdmin, onSaved }) => {
  const [name, setName] = useState('');
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Initialize form when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setIsCompanyAdmin(user.is_company_admin || false);
      setSelectedProducts(user.seats || []);
    }
  }, [user]);

  const handleProductToggle = async (productKey) => {
    if (!user) return;

    const currentlyHas = selectedProducts.includes(productKey);
    setSaving(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();

      if (currentlyHas) {
        // Find the seat to revoke
        const seat = user.seatDetails?.find(s => s.product === productKey);
        if (seat) {
          const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/seats/${seat.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Failed to revoke seat');
          }

          setSelectedProducts(prev => prev.filter(p => p !== productKey));
          setSuccess(`${PRODUCTS.find(p => p.key === productKey)?.label} access removed`);
        }
      } else {
        // Assign new seat
        const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/seats`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: user.id,
            product: productKey
          })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to assign seat');
        }

        setSelectedProducts(prev => [...prev, productKey]);
        setSuccess(`${PRODUCTS.find(p => p.key === productKey)?.label} access granted`);
      }

      // Clear success message after delay
      setTimeout(() => setSuccess(''), 3000);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();

      // Update user profile (name and admin status)
      const updateData = { name: name.trim() };
      if (isSuperAdmin) {
        updateData.is_company_admin = isCompanyAdmin;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }

      setSuccess('Profile updated successfully');
      setTimeout(() => {
        setSuccess('');
        if (onSaved) onSaved();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setConfirmRemoveOpen(false);
    onClose();
  };

  const handleRemoveUser = async () => {
    if (!user) return;

    setRemoving(true);
    setError('');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/my-company/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove user');
      }

      setSuccess('User removed from team');
      setConfirmRemoveOpen(false);

      // Close dialog and refresh after short delay
      setTimeout(() => {
        if (onSaved) onSaved();
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setConfirmRemoveOpen(false);
    } finally {
      setRemoving(false);
    }
  };

  // Check if this is the current user (can't remove yourself)
  const isCurrentUser = user?.id === currentUser?.uid;

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: vortxColors.teal,
              fontSize: '1.25rem'
            }}
          >
            {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Team Member
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
            {success}
          </Alert>
        )}

        {/* Profile Section */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
          Profile Information
        </Typography>
        <TextField
          label="Full Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          sx={{ mb: 2 }}
        />

        {/* Member Since */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Member since:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {user.created_at
              ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              : '—'}
          </Typography>
        </Box>

        {/* Last Activity */}
        {user.last_login_at && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Last active:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {new Date(user.last_login_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
        )}

        {/* Company Admin Toggle (Super Admin only) */}
        {isSuperAdmin && !user.is_super_admin && (
          <FormControlLabel
            control={
              <Switch
                checked={isCompanyAdmin}
                onChange={(e) => setIsCompanyAdmin(e.target.checked)}
                disabled={saving}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: vortxColors.teal
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: vortxColors.teal
                  }
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>Company Admin</Typography>
                <Typography variant="caption" color="text.secondary">
                  Can manage team members and seats
                </Typography>
              </Box>
            }
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
          />
        )}

        <Button
          variant="contained"
          onClick={handleSaveProfile}
          disabled={saving}
          size="small"
          sx={{
            bgcolor: vortxColors.gold,
            color: vortxColors.green,
            mb: 3,
            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
          }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Profile'}
        </Button>

        <Divider sx={{ mb: 2 }} />

        {/* Product Access Section */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Product Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Toggle access to each product. Changes are saved immediately.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {PRODUCTS.map((product) => {
            const hasAccess = selectedProducts.includes(product.key);
            return (
              <Paper
                key={product.key}
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderColor: hasAccess ? product.color : 'divider',
                  bgcolor: hasAccess ? alpha(product.color, 0.05) : 'transparent'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={product.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(product.color, 0.15),
                      color: product.color,
                      fontWeight: 600
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {hasAccess ? 'Has access' : 'No access'}
                  </Typography>
                </Box>
                <Switch
                  checked={hasAccess}
                  onChange={() => handleProductToggle(product.key)}
                  disabled={saving}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: product.color
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: product.color
                    }
                  }}
                />
              </Paper>
            );
          })}
        </Box>

        {/* Remove from Team Section */}
        {!isCurrentUser && !user.is_super_admin && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f44336' }}>
                  Remove from Team
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This will revoke all product access and remove the user from your organization.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<PersonRemoveIcon />}
                onClick={() => setConfirmRemoveOpen(true)}
                disabled={saving || removing}
              >
                Remove
              </Button>
            </Box>
          </>
        )}

        {/* Remove Confirmation Dialog */}
        <Dialog
          open={confirmRemoveOpen}
          onClose={() => setConfirmRemoveOpen(false)}
          maxWidth="xs"
        >
          <DialogTitle sx={{ color: '#f44336' }}>Remove Team Member?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to remove <strong>{user.name || user.email}</strong> from your team?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will revoke all their product access. They can be re-invited later.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)} disabled={removing}>
              Cancel
            </Button>
            <Button
              onClick={handleRemoveUser}
              color="error"
              variant="contained"
              disabled={removing}
            >
              {removing ? <CircularProgress size={20} /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamSeatsPage;
